export type Role = "owner" | "developer" | "viewer" | "operator" | "admin" | string;

export type ApiErrorBody = {
  error: {
    code: string;
    message: string;
    request_id?: string;
    retryable?: boolean;
  };
};

export type Features = {
  email_delivery: boolean;
  live_rental: boolean;
  mac_diagnostics: boolean;
};

export type User = {
  id: string;
  email: string;
  name: string;
  role: Role;
};

export type Tenant = {
  id: string;
  name: string;
};

export type SessionPayload = {
  user: User;
  tenant: Tenant;
  csrf_token: string;
  features: Features;
};

export type Machine = {
  id: string;
  display_name: string;
  model: string | null;
  ram_bytes: number | null;
  storage_bytes: number | null;
  lifecycle: string;
  health: string;
  last_seen_at: string | null;
  mode: "demo" | "live";
  rentable: boolean;
};

export type Project = {
  id: string;
  name: string;
  description: string;
  created_at: string;
};

export type ProjectActivityWindow = "24h" | "7d";

export type ProjectActivitySeriesPoint = {
  at: string;
  created: number;
  succeeded: number;
  failed: number;
};

export type ProjectActivityRecentTask = {
  id: string;
  title: string;
  state: string;
  kind: string;
  updated_at: string;
};

export type ProjectActivity = {
  project_id: string;
  observed_at: string;
  window: ProjectActivityWindow;
  source: "task_database";
  capabilities: {
    application_metrics: false;
    archive_restore: false;
  };
  tasks: {
    total: number;
    queued: number;
    running: number;
    cancel_requested: number;
    succeeded: number;
    failed: number;
    canceled: number;
  };
  workspace_requests: {
    requested: number;
    reviewing: number;
    declined: number;
    canceled: number;
  };
  series: ProjectActivitySeriesPoint[];
  recent_tasks: ProjectActivityRecentTask[];
};

export type ServiceHealth = "pending" | "healthy" | "degraded" | "unavailable" | "stale";

export type ServiceSampleCollection = "ok" | "unavailable";

export type ServiceSample = {
  id: string;
  observed_at: string;
  received_at: string;
  collection: ServiceSampleCollection;
  probe_success: boolean | null;
  probe_duration_ms: number | null;
  desired_replicas: number | null;
  ready_replicas: number | null;
};

/** Observed Kubernetes rollout state — not a deploy command or traffic signal. */
export type ServiceReleaseState = "progressing" | "available" | "failed";

export type ServiceReleaseObservation = {
  id: string;
  revision: string;
  state: ServiceReleaseState;
  observed_at: string;
};

export type ProjectService = {
  id: string;
  name: string;
  source: "kubernetes_probe";
  health: ServiceHealth;
  latest: ServiceSample | null;
  series: ServiceSample[];
  /** Newest first, max 30. Optional while older API builds omit the field. */
  releases?: ServiceReleaseObservation[];
};

export type ProjectServicesPage = {
  observed_at: string;
  capabilities: {
    deployment_control: false;
    http_traffic: false;
  };
  items: ProjectService[];
};

/** In-app project alert reasons from observed service health. */
export type ProjectAlertReason = "degraded" | "unavailable" | "stale";

export type ProjectAlertStatus = "open" | "resolved";

export type ProjectAlert = {
  id: string;
  service_id: string;
  service_name: string;
  reason: ProjectAlertReason;
  status: ProjectAlertStatus;
  opened_at: string;
  resolved_at: string | null;
  acknowledged_at: string | null;
};

export type ProjectAlertsPage = {
  observed_at: string;
  enabled: boolean;
  items: ProjectAlert[];
};

export type ProjectAlertPreferences = {
  enabled: boolean;
};

export type ProjectAlertAcknowledgeResult = {
  id: string;
  acknowledged_at: string;
};

export type TaskKind = "plan" | "diagnostics";

export type TaskFilterState =
  | "queued"
  | "running"
  | "cancel_requested"
  | "succeeded"
  | "failed"
  | "canceled";

export type Task = {
  id: string;
  project_id: string;
  title: string;
  instructions: string;
  kind: TaskKind;
  state: string;
  created_at: string;
  updated_at: string;
  result: Record<string, unknown> | null;
  error: string | null;
};

export type TaskListQuery = {
  q?: string;
  state?: TaskFilterState;
  project_id?: string;
  page?: number;
  limit?: number;
};

export type TaskListPage = {
  items: Task[];
  total: number;
  page: number;
  limit: number;
};

export type TaskEvent = {
  id: string;
  state: string;
  created_at: string;
};

export type TaskEventsResponse = {
  items: TaskEvent[];
  history_scope: "since_tracking_enabled";
};

export const TASK_TERMINAL = new Set(["succeeded", "failed", "canceled"]);
export const TASK_FILTER_STATES: TaskFilterState[] = [
  "queued",
  "running",
  "cancel_requested",
  "succeeded",
  "failed",
  "canceled",
];

export type Lease = {
  id: string;
  machine_id: string;
  state: string;
  mode: "demo";
  created_at: string;
  updated_at: string;
};

export type Operation = {
  id: string;
  state: string;
  resource_id: string | null;
  kind: string;
  created_at: string;
  updated_at: string;
  error: ApiErrorBody["error"] | null;
};

export type Dashboard = {
  counts: {
    projects: number;
    tasks: number;
    active_leases: number;
  };
  recent_tasks: Task[];
  mode: "pilot";
};

export type OperatorOverview = {
  machines: Machine[];
  counts: {
    users: number;
    tenants: number;
    tasks: number;
  };
  recent_events: Array<{
    id: string;
    action: string;
    created_at: string;
  }>;
};

export type AdminCabinet = OperatorOverview & {
  network: { enforced: true; ranges: string[] };
  controls: { maintenance: boolean; workspace_review: boolean; enrollment: boolean };
  disconnected: string[];
};

export type EnrollmentReceipt = {
  id: string;
  connector_token: string;
  shown_once: boolean;
  lifecycle: string;
  rentable: boolean;
};

export type AuditPage = {
  items: Array<{
    id: string;
    action: string;
    created_at: string;
  }>;
};

export type IntegrationProvider = "openai" | "anthropic" | "xai" | "google" | "github" | "gitlab";

export type IntegrationVerification = "not_checked" | "verified" | "failed" | "stale";

export type Integration = {
  id: string;
  name: string;
  provider: IntegrationProvider;
  status: string;
  last_checked_at: string | null;
  created_at: string;
  version: number;
};

export type ProjectConnection = Integration & {
  verification: IntegrationVerification;
};

export type ProjectConnectionsPage = {
  items: ProjectConnection[];
  capabilities: {
    checklist: true;
    ai_execution: false;
    repository_execution: false;
  };
  verification_max_age_seconds: number;
};

export type IntegrationDeleteResult = {
  ok: true;
  provider_key_revoked: false;
};

export type WorkspaceProfileId = "interactive" | "ios-build" | "agent-workspace";

export type WorkspaceMemoryGb = 16 | 24 | 32 | 64 | 128;

export type WorkspaceRequestState = "requested" | "reviewing" | "declined" | "canceled";

export type WorkspaceProfile = {
  id: WorkspaceProfileId;
  version: number;
  requested_tools: string[];
  availability: "request_only";
};

export type WorkspaceProfilesPage = {
  items: WorkspaceProfile[];
  capabilities: {
    provisioning: false;
    billing: false;
  };
};

export type WorkspaceRequest = {
  id: string;
  project_id: string;
  profile: WorkspaceProfileId;
  profile_version: number;
  memory_gb: WorkspaceMemoryGb;
  storage_gb: number;
  duration_days: number;
  notes: string;
  state: WorkspaceRequestState;
  review_note: string;
  version: number;
  created_at: string;
  updated_at: string;
};

export type WorkspaceRequestPage = {
  items: WorkspaceRequest[];
  total: number;
  page: number;
  limit: number;
  capabilities: {
    provisioning: false;
    billing: false;
  };
};

export type OperatorWorkspaceRequest = WorkspaceRequest & {
  tenant_name: string;
  project_name: string;
};

export type OperatorWorkspaceRequestPage = {
  items: OperatorWorkspaceRequest[];
  total: number;
  page: number;
  limit: number;
};

export type WorkspaceRequestCreate = {
  profile: WorkspaceProfileId;
  memory_gb: WorkspaceMemoryGb;
  storage_gb: number;
  duration_days: number;
  notes?: string;
};

/** GET …/workspace-requests/:id/plan — server-owned planning snapshot or legacy preview. */
export type WorkspacePlanStep = {
  action: string;
  state: string;
};

export type WorkspacePlan = {
  schema_version: number;
  mode: "planning-only";
  executable: false;
  profile: WorkspaceProfileId;
  profile_version: number;
  requested_tools: string[];
  steps: WorkspacePlanStep[];
  blockers: string[];
  capabilities: {
    provisioning: false;
    ai_execution: false;
    archive_restore: false;
  };
};

export type WorkspaceRequestPlanResponse = {
  request_id: string;
  request_state: WorkspaceRequestState;
  plan: WorkspacePlan;
  persisted: boolean;
  created_at: string | null;
};

/** POST /account/password — acknowledgment only; session is refreshed separately. */
export type PasswordChangeResult = {
  ok: true;
};

export type Health = {
  status: string;
  version: string;
};

export type CapacityProduct = "project-storage" | "archive" | "backup";

export type CapacityRequest = {
  id: string;
  product: CapacityProduct;
  amount_gb: number;
  notes: string | null;
  state: "requested" | "canceled";
  created_at: string;
};

export type EnvironmentCatalogItem = {
  id: CapacityProduct;
  unit: "GB";
  price: null;
  availability: "request";
};

export type TenantEnvironment = {
  scope: "tenant";
  mode: "pilot";
  summary: {
    projects: number;
    queued_tasks: number;
    running_tasks: number;
    completed_tasks: number;
    connections: number;
    active_demo_leases: number;
  };
  resources: unknown[];
  requests: CapacityRequest[];
  catalog: EnvironmentCatalogItem[];
  capabilities: {
    live_compute: boolean;
    storage_provisioning: boolean;
    paid_checkout: boolean;
  };
};

export type AccountSession = {
  id: string;
  created_at: string;
  expires_at: string;
  current: boolean;
};

export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly retryable: boolean;

  constructor(
    status: number,
    code: string,
    message: string,
    requestId?: string,
    retryable = false,
  ) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.retryable = retryable;
  }
}

export const OPERATION_TERMINAL = new Set([
  "succeeded",
  "failed",
  "canceled",
]);
